import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDateTime } from '@/utils/date';
import type { TeacherInactivationRequest } from '../types/teacherInactivation.types';
import { TeacherInactivationStatusBadge } from './TeacherInactivationStatusBadge';

export const TEACHER_COLUMN_ID = 'teacherName';

export function getTeacherInactivationColumns(
  searchText: string | undefined,
): ColumnDef<TeacherInactivationRequest>[] {
  const highlightSearchText = (text: string | null) =>
    text ? <HighlightText text={text} highlightedText={searchText} /> : undefined;

  return [
    createSerialColumn<TeacherInactivationRequest>(),
    {
      id: TEACHER_COLUMN_ID,
      header: 'Teacher',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightSearchText(row.original.teacherName)}
          primaryClassName="font-medium"
          secondary={highlightSearchText(row.original.teacherMobile)}
        />
      ),
    },
    {
      accessorKey: 'teacherDesignation',
      header: 'Designation',
      cell: ({ row }) =>
        row.original.teacherDesignation ? (
          <WrappingCellText>{row.original.teacherDesignation}</WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <WrappingCellText>{highlightSearchText(row.original.instituteName)}</WrappingCellText>
      ),
    },
    {
      id: 'code',
      header: 'Code / EIIN',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightSearchText(row.original.instituteCode) ?? <EmptyValue />}
          secondary={highlightSearchText(row.original.eiinNumber)}
        />
      ),
    },
    {
      id: 'thanaName',
      header: 'Thana / District',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.thanaName ?? <EmptyValue />}
          secondary={row.original.districtName}
        />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <TeacherInactivationStatusBadge status={row.original.status} />,
    },
    {
      id: 'requesterName',
      header: 'Submitted By',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightSearchText(row.original.requesterName)}
          primaryClassName="font-medium"
          secondary={highlightSearchText(row.original.requesterHrEmpId)}
        />
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted At',
      cell: ({ row }) => (
        <span className="whitespace-nowrap">{formatDisplayDateTime(row.original.createdAt)}</span>
      ),
    },
    {
      accessorKey: 'amName',
      header: 'AM Name',
      cell: ({ row }) =>
        row.original.amName ? (
          <WrappingCellText>{row.original.amName}</WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    createActionsColumn<TeacherInactivationRequest>((request) => (
      <RowViewLink
        to="/settings/teacher-inactivations/$requestId"
        params={{ requestId: request.id }}
        aria-label={`View ${request.teacherName}`}
      />
    )),
  ];
}
